import axios, { AxiosResponse } from 'axios';

import { getCognitoSession } from './authUtils';

// Path the dashboard is always mounted at on a Zendesk-provisioned tenant
// (matches BASE_PATH in zd.*.env). Hardcoded here because the redirect
// destination is always a Zendesk tenant — regardless of which build the
// redirect originates from (public, internal, zd) — so we can't rely on
// the *current* build's BASE_PATH (which is empty for public/internal/
// e2e/local/vitest).
export const ZENDESK_DASHBOARD_PATH = '/forethought/dashboard';

interface ValidateEmailResponse {
  // A url where the user can find their company's dashboard
  login_url: string;
  // Populated when the user's org is ZD-provisioned and matched via subdomain.
  // Frontend uses these to build the buildZendeskSignInUrl redirect.
  is_zendesk_provisioned?: boolean;
  subdomain?: string;
}

/**
 * Dashboard root for the ZD-provisioned tenant at `subdomain` — the URL the
 * user should land on after they successfully sign in there. Lands on the
 * tenant subdomain of the build's Zendesk apex domain (prod: `zendesk.com`,
 * non-prod: `zendesk-staging.com`), under the fixed Zendesk dashboard mount.
 */
export const getSuccessfullySignedInUrl = (subdomain: string): string => {
  return `${window.location.protocol}//${subdomain}.${ZENDESK_DOMAIN}${ZENDESK_DASHBOARD_PATH}`;
};

/**
 * Build the Zendesk-provisioned sign-in redirect. Sends the user to the
 * ZD-tenant subdomain's auth flow; after sign-in, ZD returns them to
 * `returnTo` (defaults to that same subdomain's dashboard root).
 *
 * The auth path is served at the tenant's origin root — NOT under the
 * dashboard mount (`/forethought/dashboard`). Only "in-dashboard" URLs
 * (the return_to after sign-in) carry that prefix.
 */
export const buildZendeskSignInUrl = (
  subdomain: string,
  returnTo: string = getSuccessfullySignedInUrl(subdomain),
): string => {
  const origin = `${window.location.protocol}//${subdomain}.${ZENDESK_DOMAIN}`;
  return `${origin}/auth/v3/signin?return_to=${encodeURIComponent(returnTo)}`;
};

export const validateAssistEmail = (
  email: string,
  subdomain?: string,
): Promise<AxiosResponse<ValidateEmailResponse>> => {
  return axios.post(`${ASSIST_API_URL}/assist-extension/v1/validate-email`, {
    email,
    ...(subdomain ? { subdomain } : {}),
  });
};

/**
 * Pull current session from Amplify and post message to pass to Assist
 * In the case no extension is available, this is fine and this will no-op
 */
export async function sendSessionToAssistExtension() {
  // Noop if chrome is undefined
  if (!window.chrome?.runtime?.sendMessage || IS_ZENDESK_BUILD) {
    return;
  }
  let session = null;
  try {
    session = await getCognitoSession();
  } catch {}
  try {
    await window.chrome.runtime.sendMessage(ASSIST_EXTENSION_ID_PRODUCTION, {
      session,
      type: 'COGNITO_AUTH_EVENT',
    });
  } catch {
    // If this raises an exception it means the extension is not available. This is fine, consider a no-op
  }
  try {
    await window.chrome.runtime.sendMessage(ASSIST_EXTENSION_ID_INTERNAL, {
      session: null,
      type: 'COGNITO_AUTH_EVENT',
    });
  } catch {
    // If this raises an exception it means the internal extension is not available. This is fine, consider a no-op
  }
}

interface SetAuthCookiePayload {
  email?: string;
  subdomain?: string;
}

const sendToBothAssistExtensions = async (message: unknown) => {
  if (!window.chrome?.runtime?.sendMessage || !IS_ZENDESK_BUILD) {
    return;
  }
  for (const id of [
    ASSIST_EXTENSION_ID_PRODUCTION,
    ASSIST_EXTENSION_ID_INTERNAL,
  ]) {
    try {
      await window.chrome.runtime.sendMessage(id, message);
    } catch {
      // Extension not installed — no-op.
    }
  }
};

/**
 * Provision the Assist extension with the customer's ZD subdomain + the
 * user's email. The extension persists both stickily and uses subdomain to
 * route API requests through the customer's ZD proxy.
 *
 * We deliberately don't ship cookies in this payload: the Zendesk SSO
 * cookies are HttpOnly, so `document.cookie` can't see them. The extension
 * obtains its own cookies through its content-script context.
 */
export async function sendAuthCookiesToAssistExtension(
  payload: SetAuthCookiePayload,
) {
  await sendToBothAssistExtensions({
    email: payload.email,
    subdomain: payload.subdomain,
    type: 'SET_AUTH_COOKIE',
  });
}

/**
 * Tell the Assist extension to drop its ZD session cookies and flip
 * is_provisioned back to false. Sticky values (subdomain, email) are preserved
 * so the next sign-in URL can still pre-fill.
 */
export async function clearAssistExtensionCookies() {
  await sendToBothAssistExtensions({ type: 'CLEAR_AUTH_COOKIE' });
}

// Subdomains that aren't actual customer tenants — don't ship these to the
// extension as zendesk_subdomain. Mirrors the list in InitApp.componentDidMount
// where org-discovery is also skipped for these.
const NON_TENANT_SUBDOMAINS = new Set([
  'dashboard-int',
  'dashboard-stage-int',
  'localhost',
]);

/**
 * Send the signed-in user's email + the dashboard's tenant subdomain to the
 * Assist extension. The extension persists both stickily (in
 * `chrome.storage.local`) and uses them to:
 *   - pre-fill the email field on the sign-in page after a switch-account /
 *     401 round-trip (via ?email= on GET_STARTED_URL),
 *   - route API requests through the customer's ZD subdomain proxy. The
 *     extension owns its own cookie acquisition (HttpOnly cookies are
 *     invisible to this dashboard); we only forward identity context.
 *
 * No-ops when there's no signed-in session.
 */
export async function sendUserContextToAssistExtension() {
  if (!window.chrome?.runtime?.sendMessage) {
    return;
  }
  let session = null;
  try {
    session = await getCognitoSession();
  } catch {
    return;
  }
  if (!session) {
    return;
  }

  let email: string | undefined;
  try {
    email = session.getIdToken().payload?.email as string | undefined;
  } catch {}

  const hostnameSubdomain = window.location.hostname.split('.')[0];
  const subdomain = NON_TENANT_SUBDOMAINS.has(hostnameSubdomain)
    ? undefined
    : hostnameSubdomain;

  if (!email && !subdomain) {
    return;
  }
  await sendAuthCookiesToAssistExtension({ email, subdomain });
}

/**
 * Zendesk-build analog of `sendUserContextToAssistExtension`. There's no
 * Cognito session in the Zendesk build, so the email is supplied by the
 * caller (sourced from `getZendeskUserInfo`); subdomain still comes from
 * the page's leading hostname segment.
 *
 * Same downstream effect: the Assist extension receives `email` +
 * `subdomain` via `sendAuthCookiesToAssistExtension` so it can pre-fill
 * the sign-in page and (eventually) route API requests through the
 * customer's ZD subdomain proxy.
 */
export async function sendZendeskUserContextToAssistExtension(
  email: string | undefined,
): Promise<void> {
  if (!window.chrome?.runtime?.sendMessage) {
    return;
  }

  const hostnameSubdomain = window.location.hostname.split('.')[0];
  const subdomain = NON_TENANT_SUBDOMAINS.has(hostnameSubdomain)
    ? undefined
    : hostnameSubdomain;

  if (!email && !subdomain) {
    return;
  }
  await sendAuthCookiesToAssistExtension({ email, subdomain });
}
